import React, { useState, useMemo } from "react";
import {
  X,
  Database,
  CheckCircle2,
  AlertCircle,
  Package,
  Truck,
  User,
  MapPin,
  Calendar,
  Loader2,
  Sparkles,
  ClipboardPaste,
  Hash
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../service/api";

interface ManualOrderSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export interface ParsedSyncOrder {
  externalId: string;
  orderNumber: string;
  invoiceNumber?: string | null;
  description?: string;
  cariKod: string;
  depoKod: string;
  orderDate: string;
  deliveryDate?: string;
  totalAmount: number;
  shippingAddressExternalId?: string | null;
  billingAddressExternalId?: string | null;
  shippingAddress?: string | null;
  billingAddress?: string | null;
  shipment?: string | null;
  currency: string;
  exchangeRate: number;
  isGuest?: boolean;
  guestName?: string | null;
  guestPhone?: string | null;
  guestTaxNumber?: string | null;
  source?: string;
  items: Array<{
    externalId: string;
    stockCode: string;
    quantity: number;
    price: number;
    taxRate: number;
    taxAmount: number;
    total: number;
    description?: string;
  }>;
}

export const parseSqlTsvData = (headerRaw: string, itemsRaw: string): ParsedSyncOrder | null => {
  // 1. Akıllı Birleştirilmiş Metin Desteği (Kullanıcı tek bir kutuya her şeyi yapıştırdıysa)
  let finalHeaderRaw = headerRaw.trim();
  let finalItemsRaw = itemsRaw.trim();

  if (!finalItemsRaw && finalHeaderRaw.includes("\n")) {
    const lines = finalHeaderRaw.split("\n").map(l => l.trim()).filter(Boolean);
    const headerLines: string[] = [];
    const itemLines: string[] = [];

    for (const line of lines) {
      const cols = line.split("\t");
      if (cols.length >= 14) {
        headerLines.push(line);
      } else if (cols.length >= 3) {
        itemLines.push(line);
      }
    }

    if (headerLines.length > 0) {
      finalHeaderRaw = headerLines[0];
      finalItemsRaw = itemLines.join("\n");
    }
  }

  if (!finalHeaderRaw) return null;

  const headerLine = finalHeaderRaw.split("\n").map(l => l.trim()).filter(Boolean)[0];
  if (!headerLine) return null;

  const hCols = headerLine.split("\t");
  if (hCols.length < 5) return null;

  const externalId = hCols[0]?.trim() || "";
  const orderNumber = hCols[1]?.trim() || externalId;
  const invoiceNumber = hCols[2]?.trim() || null;
  const description = hCols[3]?.trim() || "";
  const cariKod = hCols[4]?.trim() || "";
  const depoKod = hCols[5]?.trim() || "1";
  const orderDate = hCols[6]?.trim() || new Date().toISOString();
  const deliveryDate = hCols[7]?.trim() || orderDate;
  const totalAmount = parseFloat(hCols[8]?.trim().replace(",", ".")) || 0;
  const shippingAddressExternalId = hCols[9]?.trim() || null;
  const billingAddressExternalId = hCols[10]?.trim() || null;
  const shippingAddress = hCols[11]?.trim() || null;
  const billingAddress = hCols[12]?.trim() || null;
  const shipment = hCols[13]?.trim() || "-";
  const currency = hCols[14]?.trim() || "TRY";
  const exchangeRate = parseFloat(hCols[15]?.trim().replace(",", ".")) || 2;
  const isGuest = hCols[16]?.trim() === "1" || hCols[16]?.trim() === "true" || cariKod.startsWith("SANAL-");
  const guestName = hCols[17]?.trim() || null;
  const guestPhone = hCols[18]?.trim() || null;
  const guestTaxNumber = hCols[19]?.trim() || null;
  const source = hCols[20]?.trim() || "SQL_IMPORT";

  const items: ParsedSyncOrder["items"] = [];
  const itemLines = finalItemsRaw.split("\n").map(l => l.trim()).filter(Boolean);

  for (const line of itemLines) {
    const iCols = line.split("\t");
    if (iCols.length >= 3) {
      const itemExtId = iCols[0]?.trim() || "";
      const stockCode = iCols.length >= 4 ? iCols[2]?.trim() : iCols[1]?.trim();
      const quantity = parseFloat((iCols.length >= 4 ? iCols[3] : iCols[2])?.trim().replace(",", ".")) || 1;
      const price = parseFloat((iCols.length >= 5 ? iCols[4] : iCols[3])?.trim().replace(",", ".")) || 0;
      const taxRate = parseFloat((iCols.length >= 6 ? iCols[5] : "20")?.trim().replace(",", ".")) || 20;
      const taxAmount = parseFloat((iCols.length >= 7 ? iCols[6] : "0")?.trim().replace(",", ".")) || 0;
      const total = parseFloat((iCols.length >= 8 ? iCols[7] : "0")?.trim().replace(",", ".")) || (quantity * price);

      if (stockCode) {
        items.push({
          externalId: itemExtId || `${externalId}_${stockCode}`,
          stockCode,
          quantity,
          price,
          taxRate,
          taxAmount,
          total,
        });
      }
    }
  }

  return {
    externalId,
    orderNumber,
    invoiceNumber,
    description,
    cariKod,
    depoKod,
    orderDate,
    deliveryDate,
    totalAmount,
    shippingAddressExternalId,
    billingAddressExternalId,
    shippingAddress,
    billingAddress,
    shipment,
    currency,
    exchangeRate,
    isGuest,
    guestName,
    guestPhone,
    guestTaxNumber,
    source,
    items,
  };
};

export const ManualOrderSyncModal: React.FC<ManualOrderSyncModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [headerText, setHeaderText] = useState("");
  const [itemsText, setItemsText] = useState("");
  const [loading, setLoading] = useState(false);

  const parsedOrder = useMemo(() => {
    return parseSqlTsvData(headerText, itemsText);
  }, [headerText, itemsText]);

  if (!isOpen) return null;

  const handlePasteClipboard = async (type: "header" | "items") => {
    try {
      const text = await navigator.clipboard.readText();
      if (type === "header") {
        setHeaderText(text);
      } else {
        setItemsText(text);
      }
    } catch {
      toast.info("Panodan okunamadı, lütfen alana manuel yapıştırın.");
    }
  };

  const handleSave = async () => {
    if (!parsedOrder) {
      toast.error("Lütfen geçerli bir sipariş satırı yapıştırın.");
      return;
    }

    if (parsedOrder.items.length === 0) {
      toast.warning("Sipariş için henüz ürün kalemi eklenmedi!");
    }

    setLoading(true);
    try {
      // Doğrudan /sync/orders endpointine OrderSyncDTO dizisi olarak gönderiyoruz
      const response = await api.post("/sync/orders", [parsedOrder]);
      toast.success(response.data?.message || "Sipariş ve Depo Görevi başarıyla içeri aktarıldı!");
      
      setHeaderText("");
      setItemsText("");
      onSuccess?.();
      onClose();
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Sipariş eklenirken hata oluştu.";
      toast.error(`Hata: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-card border border-borderColor rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden text-text select-none">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-borderColor bg-bgAlt/40">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-primary/10 text-primary border border-primary/20">
              <Database size={20} />
            </div>
            <div>
              <h2 className="text-base font-black text-text tracking-tight flex items-center gap-2">
                SQL / TSV İle Manuel Sipariş Ekle
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/15 text-primary font-bold">
                  Hızlı Sync
                </span>
              </h2>
              <p className="text-xs text-text/50 font-medium">
                ERP SQL sorgusu sonuçlarını doğrudan yapıştırarak anında sipariş ve toplama görevi oluşturun.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text/50 hover:text-text bg-inputBg border border-inputBorder hover:bg-inputHover rounded-xl transition-all"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex flex-col gap-5 flex-1">
          {/* Giriş Alanları */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 1. Sipariş Başlık Satırı */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-text flex items-center gap-1.5">
                  <Hash size={14} className="text-primary" />
                  Sipariş Satırı (SQL TSV)
                </label>
                <button
                  type="button"
                  onClick={() => handlePasteClipboard("header")}
                  className="text-[11px] font-semibold text-primary hover:underline flex items-center gap-1"
                >
                  <ClipboardPaste size={12} />
                  Panodan Yapıştır
                </button>
              </div>
              <textarea
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value)}
                placeholder={`Örnek:\n284271\t20260817262511\tYSF2500000035316\t\t91276\t1\t2026-08-15 10:44:15...`}
                className="w-full h-28 p-3 text-xs font-mono bg-inputBg border border-inputBorder rounded-2xl focus:outline-none focus:border-primary/50 focus:ring-4 focus:ring-primary/10 text-inputText placeholder:text-text/30 resize-none custom-scrollbar"
              />
            </div>

            {/* 2. Sipariş Kalemleri Satırları */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-text flex items-center gap-1.5">
                  <Package size={14} className="text-emerald-500" />
                  Kalem Satırları (SQL TSV)
                </label>
                <button
                  type="button"
                  onClick={() => handlePasteClipboard("items")}
                  className="text-[11px] font-semibold text-primary hover:underline flex items-center gap-1"
                >
                  <ClipboardPaste size={12} />
                  Panodan Yapıştır
                </button>
              </div>
              <textarea
                value={itemsText}
                onChange={(e) => setItemsText(e.target.value)}
                placeholder={`Örnek:\n284271_BİB\t284271\tBİB\t2.00\t62.325\t20.00\t20.78\t124.65\n284271_CS129-1\t284271\tCS129-1\t53.00\t29.185...`}
                className="w-full h-28 p-3 text-xs font-mono bg-inputBg border border-inputBorder rounded-2xl focus:outline-none focus:border-primary/50 focus:ring-4 focus:ring-primary/10 text-inputText placeholder:text-text/30 resize-none custom-scrollbar"
              />
            </div>
          </div>

          {/* Ayrıştırılan Veri Önizleme Kartı */}
          {parsedOrder ? (
            <div className="flex flex-col gap-3 p-4 bg-bgAlt/50 border border-borderColor rounded-2xl">
              <div className="flex items-center justify-between border-b border-borderColor/50 pb-2.5">
                <span className="text-xs font-black uppercase tracking-wider text-text flex items-center gap-1.5">
                  <Sparkles size={14} className="text-primary" />
                  Ayrıştırılan Sipariş Önizlemesi
                </span>
                <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-md flex items-center gap-1">
                  <CheckCircle2 size={12} />
                  Format Geçerli
                </span>
              </div>

              {/* Temel Bilgiler Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div className="flex flex-col">
                  <span className="text-[10px] text-text/50 font-bold uppercase">Sipariş No</span>
                  <span className="font-bold text-text truncate">{parsedOrder.orderNumber}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-text/50 font-bold uppercase">Fatura No</span>
                  <span className="font-bold text-text truncate">{parsedOrder.invoiceNumber || "-"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-text/50 font-bold uppercase">Müşteri (Cari)</span>
                  <span className="font-bold text-text truncate" title={parsedOrder.guestName || parsedOrder.cariKod}>
                    {parsedOrder.guestName ? `${parsedOrder.guestName} (${parsedOrder.cariKod})` : parsedOrder.cariKod}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-text/50 font-bold uppercase">Kargo / Tutar</span>
                  <span className="font-bold text-primary truncate">
                    {parsedOrder.shipment || "-"} • {parsedOrder.totalAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {parsedOrder.currency}
                  </span>
                </div>
              </div>

              {/* Sevk Adresi Bilgisi */}
              {parsedOrder.shippingAddress && (
                <div className="flex items-start gap-1.5 text-xs text-text/70 bg-card p-2 rounded-xl border border-borderColor/40">
                  <MapPin size={14} className="text-primary shrink-0 mt-0.5" />
                  <span className="line-clamp-2">{parsedOrder.shippingAddress}</span>
                </div>
              )}

              {/* Kalemler Tablosu */}
              <div className="flex flex-col gap-1.5 mt-1">
                <div className="flex items-center justify-between text-xs font-bold text-text/60">
                  <span>Sipariş Kalemleri ({parsedOrder.items.length} Kalem)</span>
                  <span>Toplam Adet: {parsedOrder.items.reduce((sum, i) => sum + i.quantity, 0)}</span>
                </div>

                <div className="max-h-36 overflow-y-auto custom-scrollbar border border-borderColor/40 rounded-xl bg-card">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-bgAlt/80 sticky top-0 text-[10px] font-black uppercase text-text/50 border-b border-borderColor">
                      <tr>
                        <th className="py-2 px-3">#</th>
                        <th className="py-2 px-3">Stok Kodu</th>
                        <th className="py-2 px-3 text-center">Miktar</th>
                        <th className="py-2 px-3 text-right">Birim Fiyat</th>
                        <th className="py-2 px-3 text-right">Toplam</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-borderColor/30">
                      {parsedOrder.items.map((it, idx) => (
                        <tr key={it.externalId || idx} className="hover:bg-bgAlt/30">
                          <td className="py-1.5 px-3 text-text/40">{idx + 1}</td>
                          <td className="py-1.5 px-3 font-bold text-text">{it.stockCode}</td>
                          <td className="py-1.5 px-3 text-center font-bold text-primary">{it.quantity}</td>
                          <td className="py-1.5 px-3 text-right text-text/70">{it.price.toFixed(2)} TL</td>
                          <td className="py-1.5 px-3 text-right font-bold text-text">{it.total.toFixed(2)} TL</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 bg-bgAlt/20 border border-dashed border-borderColor rounded-2xl text-center text-text/40">
              <ClipboardPaste size={32} className="mb-2 opacity-50" />
              <p className="text-xs font-bold">Henüz Veri Yapıştırılmadı</p>
              <p className="text-[11px] mt-0.5">
                SQL Server veya DBeaver'dan kopyaladığınız satırı yukarıdaki kutuya yapıştırın.
              </p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-borderColor bg-bgAlt/30">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2.5 rounded-2xl bg-inputBg border border-inputBorder hover:bg-inputHover text-text text-xs font-bold transition-all active:scale-95 disabled:opacity-50"
          >
            Vazgeç
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={!parsedOrder || loading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-primary hover:bg-primary/90 text-white text-xs font-bold transition-all active:scale-95 shadow-md shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>İçeri Aktarılıyor...</span>
              </>
            ) : (
              <>
                <CheckCircle2 size={16} />
                <span>Siparişi ve Görevi Oluştur</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
